import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  connectionLabel,
  useGetConnectionCredentialTypesByTypeQuery,
  useGetConnectionsQuery,
  type ConnectionItem,
} from "features/integrationsBeta/connections";
import { useCreateSecretMutation } from "features/integrationsBeta/credentials";
import type { StepConnectionRequirement } from "features/integrationsBeta/integrationsBeta";
import { formatApiError } from "utils/formatApiError";
import { useGetConnectionTypesQuery } from "features/integrationsBeta/platformEnums";
import {
  useCreateConnectionMutation,
  useCreateProviderMutation,
  useGetIntegrationProvidersQuery,
  useLazyGetConnectionConfigSchemaByTypeQuery,
} from "features/integrationsBeta/registration";
import { humanizeKey } from "utils/humanizeKey";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useState } from "react";
import JsonCodeField from "./JsonCodeField";
import JsonSchemaForm from "./JsonSchemaForm";

type Json = Record<string, unknown>;

const NEW_VALUE = "__new__";
const NONE_VALUE = "__none__";
const NEW_PROVIDER_VALUE = "__new_provider__";

interface Props {
  clientId: number;
  // A step's declared requirement: filters the picker and locks the inline
  // create form. null = the step takes no connection (callers don't render
  // this field then); undefined = unknown (old catalog) -> unfiltered picker.
  requirement?: StepConnectionRequirement | null;
  value: number | null;
  onChange: (id: number | null) => void;
  allowNone?: boolean;
  label?: string;
}

/**
 * Connection picker driven by a step's ``connection_requirement``: offers
 * only the client's connections matching the required provider/type, plus an
 * inline "New connection..." form with the provider/type locked to the
 * requirement and a schema-driven config form.
 */
export default function ConnectionBindingField({
  clientId,
  requirement,
  value,
  onChange,
  allowNone = false,
  label = "Connection",
}: Props) {
  const dispatch = useAppDispatch();
  const { canEdit } = usePlatformRole();

  const { data: connections = [], isLoading } = useGetConnectionsQuery({
    client: clientId,
    provider_slug: requirement?.provider_slug ?? undefined,
    type: requirement?.connection_type ?? undefined,
  });
  const { data: providers = [] } = useGetIntegrationProvidersQuery();
  const { data: connectionTypes = [] } = useGetConnectionTypesQuery();
  const [fetchSchema, { data: schemaResp }] =
    useLazyGetConnectionConfigSchemaByTypeQuery();
  const [createConnection, { isLoading: creating }] =
    useCreateConnectionMutation();
  const [createProvider, { isLoading: creatingProvider }] =
    useCreateProviderMutation();
  const [createSecret, { isLoading: creatingSecret }] =
    useCreateSecretMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [providerId, setProviderId] = useState<number | "">("");
  const [newProviderMode, setNewProviderMode] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [typeDraft, setTypeDraft] = useState(
    requirement?.connection_type ?? "",
  );
  const [nameDraft, setNameDraft] = useState("");
  const [configDraft, setConfigDraft] = useState<Json>({});
  const [rawDraft, setRawDraft] = useState("{}");
  const [rawError, setRawError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  // Credentials entered alongside the config: per required group the chosen
  // type (one-of groups, e.g. SSH key vs password) and its secret draft.
  const [groupChoice, setGroupChoice] = useState<Record<number, string>>({});
  const [secretDrafts, setSecretDrafts] = useState<Record<string, Json>>({});
  // Set once the connection row exists so a failed secret write can be
  // retried without creating a duplicate connection.
  const [createdConnectionId, setCreatedConnectionId] = useState<number | null>(
    null,
  );

  const lockedProvider = useMemo(
    () =>
      requirement?.provider_slug
        ? (providers.find((s) => s.slug === requirement.provider_slug) ?? null)
        : null,
    [providers, requirement],
  );

  useEffect(() => {
    if (!showCreate) return;
    if (lockedProvider) setProviderId(lockedProvider.id);
    if (requirement?.connection_type) setTypeDraft(requirement.connection_type);
  }, [showCreate, lockedProvider, requirement]);

  const effectiveProviderSlug =
    requirement?.provider_slug ??
    providers.find((s) => s.id === providerId)?.slug ??
    "";

  useEffect(() => {
    if (!showCreate || !typeDraft) return;
    fetchSchema({ provider: effectiveProviderSlug, type: typeDraft });
  }, [showCreate, typeDraft, effectiveProviderSlug, fetchSchema]);

  const configSchema = (schemaResp?.config_schema as Json | null) ?? null;

  // Credential slots for the pair being created, so secrets can be entered
  // together with the config instead of a follow-up trip to Settings.
  const { data: slotInfo } = useGetConnectionCredentialTypesByTypeQuery(
    { provider: effectiveProviderSlug, type: typeDraft },
    { skip: !showCreate || !typeDraft },
  );
  const requiredGroups =
    slotInfo?.adapter_registered === true ? slotInfo.required_groups : [];
  const slotByType = useMemo(() => {
    const map: Record<string, { label: string; secret_schema: Json | null }> =
      {};
    (slotInfo?.credential_types ?? []).forEach((slot) => {
      map[slot.type] = {
        label: slot.label,
        secret_schema: slot.secret_schema as Json | null,
      };
    });
    return map;
  }, [slotInfo]);

  // Different provider/type = different secret shapes; drop stale drafts.
  useEffect(() => {
    setSecretDrafts({});
    setGroupChoice({});
  }, [effectiveProviderSlug, typeDraft]);

  const resetCreateForm = () => {
    setShowCreate(false);
    setCreateError(null);
    setNewProviderMode(false);
    setNewProviderName("");
    setNameDraft("");
    setSecretDrafts({});
    setGroupChoice({});
    setCreatedConnectionId(null);
  };

  const handleSelect = (selected: string) => {
    if (selected === NEW_VALUE) {
      setShowCreate(true);
      setCreateError(null);
      setCreatedConnectionId(null);
      return;
    }
    if (selected === NONE_VALUE) {
      onChange(null);
      return;
    }
    onChange(Number(selected));
  };

  const handleCreate = async () => {
    if (createdConnectionId == null) {
      if ((!providerId && !newProviderMode) || !typeDraft) return;
      if (newProviderMode && !newProviderName.trim()) return;
    }
    let config: Json = configDraft;
    if (!configSchema) {
      try {
        config = rawDraft.trim() ? JSON.parse(rawDraft) : {};
      } catch {
        setRawError("Invalid JSON.");
        return;
      }
    }
    try {
      let connectionId = createdConnectionId;
      if (connectionId == null) {
        let effectiveProviderId = providerId as number;
        if (newProviderMode) {
          const provider = await createProvider({
            name: newProviderName.trim(),
          }).unwrap();
          effectiveProviderId = provider.id;
        }
        const created: ConnectionItem = await createConnection({
          client: clientId,
          provider: effectiveProviderId,
          type: typeDraft,
          ...(nameDraft.trim() ? { name: nameDraft.trim() } : {}),
          config,
        }).unwrap();
        connectionId = created.id;
        setCreatedConnectionId(created.id);
        onChange(created.id);
      }

      // Store the secrets entered alongside the config (required groups
      // only); empty forms are skipped and can be configured in Settings.
      const failures: string[] = [];
      let secretsSaved = 0;
      for (let index = 0; index < requiredGroups.length; index += 1) {
        const group = requiredGroups[index];
        const chosenType = groupChoice[index] ?? group[0];
        const draft = secretDrafts[chosenType];
        const hasValues =
          draft &&
          Object.values(draft).some((value) => value !== "" && value != null);
        if (!hasValues) continue;
        try {
          await createSecret({
            connectionId,
            type: chosenType,
            secret: draft,
          }).unwrap();
          secretsSaved += 1;
          setSecretDrafts((m) => ({ ...m, [chosenType]: {} }));
        } catch (err) {
          failures.push(
            formatApiError(
              (err as { data?: unknown })?.data,
              `Could not save the ${chosenType} credential.`,
            ),
          );
        }
      }

      if (failures.length) {
        setCreateError(
          "Connection created, but some credentials failed: " +
            failures.join(" | ") +
            " Fix the values and press Save credentials, or finish later " +
            "in Settings.",
        );
        return;
      }

      dispatch(
        pushNotification({
          severity: "success",
          message: secretsSaved
            ? "Connection and credentials created."
            : "Connection created. Credentials can be added in Settings.",
        }),
      );
      resetCreateForm();
    } catch (err) {
      const data = (err as { data?: Record<string, unknown> })?.data;
      setCreateError(formatApiError(data, "Could not create the connection."));
    }
  };

  const requirementHint = requirement
    ? `Requires: ${requirement.provider_slug ?? "any provider"} (${requirement.connection_type})`
    : null;

  // Endpoint identity so same-provider connections are distinguishable
  // (e.g. Paycom outbound vs inbound SFTP differ only by host).
  const connectionHint = (conn: ConnectionItem): string => {
    const cfg = (conn.config ?? {}) as Record<string, unknown>;
    const parts: string[] = [];
    if (conn.name) parts.push(`${conn.provider_name} (${conn.type})`);
    if (typeof cfg.host === "string" && cfg.host) parts.push(cfg.host);
    else if (typeof cfg.base_url === "string" && cfg.base_url)
      parts.push(cfg.base_url);
    return parts.join(" — ");
  };

  const selectHelperText =
    !isLoading && connections.length === 0
      ? `No matching connections for ${
          requirement?.provider_slug ?? "any provider"
        } (${requirement?.connection_type ?? "any type"}).`
      : "Existing connections of the selected client" +
        (requirement ? " that match this step's requirement" : "") +
        " — pick one or create a new one.";

  return (
    <Stack spacing={1.5}>
      {requirementHint && (
        <Typography variant="caption" color="text.secondary">
          {requirementHint}
        </Typography>
      )}

      <TextField
        select
        size="small"
        label={label}
        value={value != null ? String(value) : allowNone ? NONE_VALUE : ""}
        onChange={(e) => handleSelect(e.target.value)}
        helperText={selectHelperText}
        SelectProps={{
          renderValue: (selected) => {
            if (selected === NONE_VALUE) return "— none —";
            const conn = connections.find(
              (c) => String(c.id) === String(selected),
            );
            if (!conn) return String(selected);
            const hint = connectionHint(conn);
            return hint
              ? `${connectionLabel(conn)} — ${hint}`
              : connectionLabel(conn);
          },
        }}
      >
        {allowNone && <MenuItem value={NONE_VALUE}>— none —</MenuItem>}
        {connections.map((conn) => (
          <MenuItem key={conn.id} value={String(conn.id)}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{connectionLabel(conn)}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
              >
                #{conn.id}
                {connectionHint(conn) ? ` — ${connectionHint(conn)}` : ""}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {canEdit && <Divider />}
        {canEdit && (
          <MenuItem value={NEW_VALUE}>+ New connection...</MenuItem>
        )}
      </TextField>

      {showCreate && canEdit && (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="body2" fontWeight={600}>
              New connection
            </Typography>

            {connections.length > 0 && (
              <Alert severity="info">
                A matching connection already exists — reuse it if it points at
                the same endpoint. Creating a second one is fine for a different
                endpoint (e.g. another SFTP host or directory): every connection
                holds its own credentials.
              </Alert>
            )}

            {lockedProvider ? (
              <TextField
                size="small"
                label="Provider"
                value={lockedProvider.name}
                disabled
              />
            ) : requirement?.provider_slug ? (
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  label="Provider"
                  value={requirement.provider_slug}
                  disabled
                  error
                  helperText={`Provider '${requirement.provider_slug}' is not registered yet.`}
                />
                <Button
                  size="small"
                  sx={{ mt: 0.5 }}
                  disabled={creatingProvider}
                  onClick={async () => {
                    try {
                      const provider = await createProvider({
                        name: humanizeKey(requirement.provider_slug as string),
                      }).unwrap();
                      if (provider.slug !== requirement.provider_slug) {
                        setCreateError(
                          `Created provider slug '${provider.slug}' does not ` +
                            `match the required '${requirement.provider_slug}'.`,
                        );
                      }
                    } catch {
                      setCreateError("Could not create the provider.");
                    }
                  }}
                >
                  Create provider "{requirement.provider_slug}"
                </Button>
              </Box>
            ) : newProviderMode ? (
              <TextField
                size="small"
                label="New provider name"
                value={newProviderName}
                onChange={(e) => setNewProviderName(e.target.value)}
                helperText="A new Provider row will be created together with the connection."
              />
            ) : (
              <TextField
                select
                size="small"
                label="Provider"
                value={providerId === "" ? "" : String(providerId)}
                onChange={(e) => {
                  if (e.target.value === NEW_PROVIDER_VALUE) {
                    setNewProviderMode(true);
                    setProviderId("");
                    return;
                  }
                  setProviderId(Number(e.target.value));
                }}
              >
                {providers.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={NEW_PROVIDER_VALUE}>+ New provider...</MenuItem>
              </TextField>
            )}

            {requirement?.connection_type ? (
              <Box>
                <Chip
                  label={`Type: ${requirement.connection_type}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            ) : (
              <TextField
                select
                size="small"
                label="Type"
                value={typeDraft}
                onChange={(e) => setTypeDraft(e.target.value)}
              >
                {connectionTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              size="small"
              label="Name (optional)"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              disabled={createdConnectionId != null}
              helperText={
                "Label to tell sibling connections apart, e.g. 'Outbound " +
                "SFTP'. Becomes part of the AWS secret path of the " +
                "credentials below."
              }
            />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Config
              </Typography>
              {configSchema ? (
                <JsonSchemaForm
                  schema={configSchema}
                  value={configDraft}
                  onChange={setConfigDraft}
                  disabled={createdConnectionId != null}
                />
              ) : (
                <JsonCodeField
                  value={rawDraft}
                  onChange={(v) => {
                    setRawDraft(v);
                    setRawError(null);
                  }}
                  error={rawError}
                  rows={5}
                />
              )}
            </Box>

            {requiredGroups.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Credentials
                </Typography>
                <Stack spacing={1.5}>
                  {requiredGroups.map((group, index) => {
                    const chosenType = groupChoice[index] ?? group[0];
                    const slot = slotByType[chosenType];
                    return (
                      <Box key={group.join("|")}>
                        {group.length > 1 && (
                          <TextField
                            select
                            size="small"
                            label="Auth method"
                            value={chosenType}
                            onChange={(e) =>
                              setGroupChoice((m) => ({
                                ...m,
                                [index]: e.target.value,
                              }))
                            }
                            sx={{ mb: 1, minWidth: 220 }}
                          >
                            {group.map((credType) => (
                              <MenuItem key={credType} value={credType}>
                                {slotByType[credType]?.label ?? credType}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        <Typography variant="caption" fontWeight={600}>
                          {slot?.label ?? chosenType} (required)
                        </Typography>
                        {slot?.secret_schema && (
                          <JsonSchemaForm
                            schema={slot.secret_schema}
                            value={secretDrafts[chosenType] ?? {}}
                            onChange={(v) =>
                              setSecretDrafts((m) => ({
                                ...m,
                                [chosenType]: v,
                              }))
                            }
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Secrets go straight to AWS Secrets Manager. Leave empty to
                  configure later in Settings; optional credential types (e.g.
                  PGP key) are added there too.
                </Typography>
              </Box>
            )}

            {createError && <Alert severity="error">{createError}</Alert>}

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleCreate}
                disabled={
                  creating ||
                  creatingProvider ||
                  creatingSecret ||
                  (createdConnectionId == null &&
                    (!typeDraft ||
                      (newProviderMode ? !newProviderName.trim() : !providerId)))
                }
              >
                {createdConnectionId != null ? "Save credentials" : "Create"}
              </Button>
              <Button size="small" onClick={resetCreateForm}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
